import type { JsonApiSpecResponse } from '../../types';

export type PageableResponse<T = any> = {
  content: T[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

type ElasticSearchHit = {
  _id: string;
  _index: string;
  _score: number;
  _source: any;
  _type?: any;
};

export function serializePageableResponse(
  data: PageableResponse
): JsonApiSpecResponse {
  return {
    data: data.content,
    meta: {
      total: data.totalElements,
      size: data.size,
      start: data.number,
    },
  };
}

interface ElasticPageableTokenResponse {
  hits: ElasticSearchHit[];
  nextToken: string;
  prevToken: string;
  totalHits: number;
}

/**
 * Reshapes an Elasticsearch page into the `{ data, meta }` shape the listings read.
 *
 * Axios runs response transforms on **failures** too, and a failure body is not a page. Reshaping
 * one turned "Could not read the claims you handle." into `{ meta: {} }` — so the only thing a
 * caller could have shown the user was gone before any error handler saw it, and every listing on
 * this serializer fell back to a generic sentence no matter what the service said.
 *
 * Axios passes the status as the third argument, which is how this tells the two apart. Called
 * directly with no status, it serializes as it always did.
 */
export function serializeElasticPageableTokenResponse(
  data: ElasticPageableTokenResponse,
  _headers?: unknown,
  status?: number
): JsonApiSpecResponse {
  if (status !== undefined && (status < 200 || status >= 300)) {
    return data as unknown as JsonApiSpecResponse;
  }
  return {
    data: data.hits,
    meta: {
      total: data.totalHits,
      nextToken: data.nextToken,
      prevToken: data.prevToken,
    },
  };
}

export const standardElasticSerializers = [
  JSON.parse,
  serializeElasticPageableTokenResponse,
];
